/**
 * The right-rail Inspector. Header shows the primitive icon and name. Body
 * renders the schema-driven form first, then any primitive-specific
 * sub-panels (EXTRACT entities, Integration actions, Document columns). A
 * separate "Run once per ..." toggle at the bottom manages explicit
 * `forEach` when it's not auto-inferred.
 */

import React from 'react';
import clsx from 'clsx';
import {
  ArrowPathRoundedSquareIcon,
  ArrowRightIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

import { useBuilder } from '~/pages/workflows/builder/state/context';
import { getPrimitive } from '~/pages/workflows/builder/fixtures/registry';
import {
  CATEGORY_ACCENT,
  CATEGORY_LABEL,
  PRIMITIVE_ICON
} from '~/pages/workflows/builder/constants';
import { createNode } from '~/pages/workflows/builder/state/reducer';
import {
  effectiveInputs,
  groupForNode,
  isEnumerableNode
} from '~/pages/workflows/builder/state/selectors';
import { issuesForNode } from '~/pages/workflows/builder/state/validation';
import type { InputFieldDescriptor, ValidationIssue } from '~/pages/workflows/builder/types';
import { SchemaForm, TextInput, BooleanInput } from './fields';
import {
  DocumentColumnsEditor,
  ExtractInspector,
  IntegrationInspector,
  InputNodeInspector,
  OutputRunModeSwitch
} from './primitive-inspectors';

export function Inspector(props: { issues: ValidationIssue[] }) {
  const { state, selectedNodeId, setSelectedNodeId, dispatch, focusField, setFocusField } =
    useBuilder();
  const node = selectedNodeId ? (state.nodes.find((n) => n.id === selectedNodeId) ?? null) : null;

  if (!node) {
    return <EmptyInspector />;
  }

  const primitive = getPrimitive(node.taskKey);
  const Icon = PRIMITIVE_ICON[node.taskKey];
  const nodeIssues = issuesForNode(props.issues, node.id);
  const errors = nodeIssues.filter((i) => i.level === 'error');
  const warnings = nodeIssues.filter((i) => i.level === 'warning');
  const enumerable = isEnumerableNode(state, node);
  const group = groupForNode(state, node.id);

  // Which DATA fields, if any, already auto-fan-out because their source is
  // a list? Used to render the "Runs once per…" badge contextually.
  const effInputs = effectiveInputs(node);

  const focusKey = focusField && focusField.nodeId === node.id ? focusField.fieldKey : null;
  React.useEffect(() => {
    if (!focusKey) return;
    const el = document.querySelector(`[data-field-id="${focusKey}"]`);
    if (el && 'scrollIntoView' in el) {
      (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    const t = setTimeout(() => setFocusField(null), 2000);
    return () => clearTimeout(t);
  }, [focusKey, setFocusField]);

  return (
    <aside className="flex h-full w-96 flex-none flex-col border-l border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800">
      {/* Header */}
      <div className="flex items-start gap-2.5 border-b border-neutral-200 dark:border-neutral-700 px-4 py-3">
        <span
          className={clsx(
            'flex h-8 w-8 flex-none items-center justify-center rounded-md ring-1 ring-inset',
            CATEGORY_ACCENT[primitive.category]
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <TextInput
              value={node.name ?? primitive.label}
              onChange={(v) => dispatch({ type: 'rename-node', nodeId: node.id, name: v })}
            />
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-neutral-500 dark:text-neutral-400">
            <span>{CATEGORY_LABEL[primitive.category]}</span>
            <span>·</span>
            <span>{primitive.label}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setSelectedNodeId(null)}
          className="rounded p-1 text-neutral-400 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
          title="Close inspector"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Issues banner */}
      {(errors.length > 0 || warnings.length > 0) && (
        <div
          className={clsx(
            'border-b px-4 py-2 text-xs',
            errors.length > 0
              ? 'border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300'
              : 'border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300'
          )}
        >
          <div className="flex items-center gap-1 font-semibold">
            <ExclamationTriangleIcon className="h-3 w-3" />
            {errors.length + warnings.length} issue
            {errors.length + warnings.length === 1 ? '' : 's'}
          </div>
          <ul className="mt-1 space-y-0.5">
            {[...errors, ...warnings].map((issue, i) => (
              <li
                key={i}
                className={clsx(
                  'flex items-start gap-1',
                  issue.fieldKey && 'cursor-pointer hover:underline'
                )}
                onClick={() =>
                  issue.fieldKey && issue.nodeId
                    ? setFocusField({ nodeId: issue.nodeId, fieldKey: issue.fieldKey })
                    : undefined
                }
              >
                <span className="mt-1 inline-block h-1 w-1 rounded-full bg-current" />
                {issue.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Enumerable status */}
      {(enumerable || group) && (
        <div className="border-b border-indigo-200 dark:border-indigo-500/30 bg-indigo-50 dark:bg-indigo-500/10 px-4 py-2 text-xs text-indigo-800 dark:text-indigo-300 flex items-start gap-1.5">
          <ArrowPathRoundedSquareIcon className="mt-0.5 h-3 w-3" />
          <div>
            Runs once per <strong>{group?.sourceLabel ?? 'item'}</strong>.
            {!node.forEach && (
              <span className="block text-[11px] text-indigo-700 dark:text-indigo-300">
                Auto-inferred because an upstream input is a list.
              </span>
            )}
          </div>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {primitive.category === 'output' && <OutputRunModeSwitch node={node} />}
        {primitive.category === 'input' ? (
          <InputNodeInspector node={node} />
        ) : (
          <SchemaForm
            nodeId={node.id}
            fields={
              primitive.category === 'output'
                ? effInputs.filter((f) => f.key !== 'runMode')
                : effInputs
            }
            focusFieldKey={focusKey}
          />
        )}
        {node.taskKey === 'EXTRACT' && <ExtractInspector node={node} />}
        {node.taskKey === 'OUTPUT_DOCUMENT' && <DocumentColumnsEditor node={node} />}
        {node.taskKey === 'OUTPUT_INTEGRATION' && <IntegrationInspector node={node} />}

        {/* ScalarLearnMore: when a DATA field is bound to a list source, offer
            one-click "Aggregate first" — surfaces as a callout under the form. */}
        <ListToScalarSuggestion
          fieldIssues={issuesForFieldList(props.issues, node.id)}
          nodeId={node.id}
        />
      </div>

      {/* Footer: explicit per-item override. Hidden when there are no
          TASK_OUTPUT bindings to iterate over (nothing upstream to enumerate),
          and hidden when the node is auto-inferred-enumerable (the top banner
          explains that case; flipping the toggle off wouldn't change anything
          because the upstream source is already a list). */}
      {(() => {
        const firstTaskBinding = node.bindings.find((b) => b.source.type === 'TASK_OUTPUT');
        const autoInferred = enumerable && !node.forEach;
        if (primitive.category === 'output') return null;
        if (!firstTaskBinding || autoInferred) return null;
        const on = Boolean(node.forEach);
        return (
          <div className="border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 px-4 py-2 text-[11px] text-neutral-600 dark:text-neutral-400 flex items-center justify-between">
            <span>Run this step for each upstream list item?</span>
            <BooleanInput
              value={on}
              onChange={(next) => {
                if (next) {
                  const b = firstTaskBinding;
                  if (b.source.type !== 'TASK_OUTPUT') return;
                  dispatch({
                    type: 'set-for-each',
                    nodeId: node.id,
                    forEach: {
                      kind: 'TASK_OUTPUT',
                      sourceNodeId: b.source.sourceNodeId,
                      sourceOutputKey: b.source.sourceOutputKey,
                      inputKey: b.inputKey
                    }
                  });
                } else {
                  dispatch({ type: 'set-for-each', nodeId: node.id, forEach: null });
                }
              }}
            />
          </div>
        );
      })()}
    </aside>
  );
}

function issuesForFieldList(issues: ValidationIssue[], nodeId: string): ValidationIssue[] {
  return issues.filter((i) => i.nodeId === nodeId && i.code === 'list-scalar-mismatch');
}

function ListToScalarSuggestion(props: { fieldIssues: ValidationIssue[]; nodeId: string }) {
  const { state, dispatch, setSelectedNodeId } = useBuilder();
  if (props.fieldIssues.length === 0) return null;
  const node = state.nodes.find((n) => n.id === props.nodeId);
  if (!node) return null;

  const insertAggregate = () => {
    // Find the offending binding
    const issue = props.fieldIssues[0];
    const binding = node.bindings.find((b) => b.inputKey === issue.fieldKey);
    if (!binding || binding.source.type !== 'TASK_OUTPUT') return;
    const fromNodeId = binding.source.sourceNodeId;
    const fromOutputKey = binding.source.sourceOutputKey;
    const aggregate = createNode('AGGREGATE');
    dispatch({
      type: 'insert-between',
      fromNodeId,
      toNodeId: node.id,
      downstreamInputKey: issue.fieldKey!,
      newNode: aggregate,
      newNodeInputKey: 'sources',
      newNodeOutputKey: 'result',
      fromOutputKey
    });
    setSelectedNodeId(aggregate.id);
  };

  return (
    <div className="rounded-md border border-indigo-200 dark:border-indigo-500/30 bg-indigo-50/60 dark:bg-indigo-500/10 p-2 text-xs text-indigo-800 dark:text-indigo-300 space-y-1.5">
      <div className="flex items-start gap-1.5">
        <InformationCircleIcon className="mt-0.5 h-3 w-3 flex-none" />
        <div>
          An upstream step produces a list, but this step expects a single value. Combine them
          first?
        </div>
      </div>
      <button
        type="button"
        onClick={insertAggregate}
        className="inline-flex items-center gap-1 rounded bg-indigo-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-indigo-700"
      >
        Insert Aggregate step
        <ArrowRightIcon className="h-3 w-3" />
      </button>
    </div>
  );
}

function EmptyInspector() {
  return (
    <aside className="flex h-full w-10 flex-none flex-col items-center justify-start border-l border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 py-3">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-neutral-400 dark:text-neutral-500 rotate-180 [writing-mode:vertical-rl]">
        Select a step
      </div>
    </aside>
  );
}

export function isInputField(field: InputFieldDescriptor): boolean {
  return field.fieldKind === 'INPUT' || field.fieldKind === 'DATA' || field.fieldKind === 'FILE';
}
